import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCommandDispatch } from '../../hooks/useCommandDispatch'
import { useAuthOptional } from '../../auth/AuthContext'
import { api } from '../../api/client'
import { SquareCodeCommandCenter } from './index'
import {
  buildDivisions,
  buildQuickAccess,
  gateFromAuth,
  mapCommandResult,
  mapDailyStatusToResult,
  validateUploadFile,
  HUB_ROOT,
  IMAGE_ACCEPT,
  CODE_EXTENSIONS,
} from './hub/commandCenterHubModel'
import type { CommandCenterHubAction, CommandResult } from './hub/CommandCenterHub.types'
import './hub/commandCenterHubRoute.css'

/**
 * Square Code V2 Command Center — route wrapper (integration seam).
 *
 * Mounted at /square/command-center under <AccessGate> (auth +
 * allowlist), OUTSIDE MainframeShell, as a full-screen surface (router.tsx).
 * It renders the approved Final Hub Center (SquareCodeCommandCenter) and wires
 * every real action to the repository:
 *
 *   - onNavigate  -> react-router navigate() to real, registered routes.
 *   - onCommand   -> useCommandDispatch().execute() (the one governed command
 *                    envelope -> POST /api/commands/dispatch), mapped from
 *                    CommandApiResult.status/message to {ok,reason}. No fake
 *                    execution; execute() also handles navigate-on-done.
 *   - onTool      -> real handlers, fail-closed where a backend is absent:
 *       video  -> api.commandCenter.meetings.status() gate; on ready, open the
 *                 real Conference Calls surface; else surface the server reason.
 *       files  -> api.files.uploadUrl + direct PUT (XHR progress) into `files`.
 *       photo  -> same upload path, image/* only, with a client-side preview.
 *       code   -> same upload path, source-file extensions (attach a source
 *                 file; there is no code executor in the repo and none is made).
 *       camera -> getUserMedia({video}) requested ONLY after Camera is chosen;
 *                 capture -> upload; unavailable/denied fails closed honestly.
 *       other  -> honest "no additional tools registered" (no fabricated action).
 *
 * Tile availability mirrors AccessGate via gateFromAuth(useAuthOptional()); the
 * hook is provider-optional so this surface also renders in SSR tests.
 */

const CONFERENCE_CALLS_ROUTE = `${HUB_ROOT}/communications/conference-calls`

type UploadState = {
  name: string
  progress: number
  status: 'uploading' | 'done' | 'error'
  reason?: string
} | null

export default function SquareCodeCommandCenterRoute() {
  const navigate = useNavigate()
  const { execute } = useCommandDispatch()
  const auth = useAuthOptional()

  const gate = gateFromAuth(
    auth ? { loading: auth.loading, user: auth.user, approved: auth.approved } : null,
  )
  const quickAccess = useMemo(() => buildQuickAccess(gate), [gate.status, gate.reason])
  const divisions = useMemo(() => buildDivisions(gate), [gate.status, gate.reason])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadValidate = useRef<(file: File) => CommandResult>(() => ({ ok: true }))
  const [upload, setUpload] = useState<UploadState>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [camera, setCamera] = useState<{ open: boolean; error?: string }>({ open: false })

  const onNavigate = useCallback((path: string) => navigate(path), [navigate])

  const onCommand = useCallback(
    async (command: string): Promise<CommandResult> => {
      const result = await execute(command).catch(() => null)
      return mapCommandResult(result)
    },
    [execute],
  )

  // Real upload: mint a signed URL, then PUT the bytes directly to Supabase
  // storage with XHR so we get genuine progress (the repo's fetch()-based
  // uploader has none). Client-side validation only — the server gates buckets.
  const uploadFile = useCallback((file: File) => {
    const check = uploadValidate.current(file)
    if (!check.ok) {
      setUpload({ name: file.name, progress: 0, status: 'error', reason: check.reason })
      return
    }
    setUpload({ name: file.name, progress: 0, status: 'uploading' })
    api.files
      .uploadUrl('files', file.name, file.type)
      .then((minted) => {
        if (!minted?.ok) {
          setUpload({ name: file.name, progress: 0, status: 'error', reason: 'Could not get an upload URL.' })
          return
        }
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', minted.signedUrl)
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUpload({ name: file.name, progress: event.loaded / event.total, status: 'uploading' })
          }
        }
        xhr.onload = () => {
          const ok = xhr.status >= 200 && xhr.status < 300
          setUpload({
            name: file.name,
            progress: 1,
            status: ok ? 'done' : 'error',
            reason: ok ? undefined : `Upload failed (${xhr.status}).`,
          })
        }
        xhr.onerror = () => setUpload({ name: file.name, progress: 0, status: 'error', reason: 'Upload failed.' })
        xhr.send(file)
      })
      .catch(() => setUpload({ name: file.name, progress: 0, status: 'error', reason: 'Could not get an upload URL.' }))
  }, [])

  const openPicker = useCallback((accept: string, validate: (file: File) => CommandResult) => {
    uploadValidate.current = validate
    const input = fileInputRef.current
    if (input) {
      input.accept = accept
      input.value = ''
      input.click()
    }
  }, [])

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const openCamera = useCallback(async (): Promise<CommandResult> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return { ok: false, reason: 'Camera is not available in this browser.' }
    }
    setCamera({ open: true })
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => {})
      }
      return { ok: true }
    } catch {
      setCamera({ open: true, error: 'Camera permission was denied.' })
      return { ok: true }
    }
  }, [])

  const closeCamera = useCallback(() => {
    stopCamera()
    setCamera({ open: false })
  }, [stopCamera])

  const capturePhoto = useCallback(() => {
    const video = videoRef.current
    if (!video || !streamRef.current) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth || 1280
    canvas.height = video.videoHeight || 720
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob((blob) => {
      if (!blob) return
      const stamp = new Date().toISOString().replace(/[:.]/g, '-')
      const file = new File([blob], `capture-${stamp}.png`, { type: 'image/png' })
      uploadValidate.current = (candidate) => validateUploadFile(candidate, { mimePrefixes: IMAGE_ACCEPT })
      uploadFile(file)
    }, 'image/png')
    stopCamera()
    setCamera({ open: false })
  }, [stopCamera, uploadFile])

  const onTool = useCallback(
    async (action: CommandCenterHubAction): Promise<CommandResult> => {
      switch (action) {
        case 'video': {
          const status = await api.commandCenter.meetings.status().catch(() => null)
          if (!status) return { ok: false, reason: 'Could not check video calling status.' }
          const mapped = mapDailyStatusToResult(status)
          if (mapped.ok) {
            navigate(CONFERENCE_CALLS_ROUTE)
            return { ok: true }
          }
          return mapped
        }
        case 'files':
          openPicker('*/*', (file) => validateUploadFile(file))
          return { ok: true }
        case 'photo':
          openPicker('image/*', (file) => validateUploadFile(file, { mimePrefixes: IMAGE_ACCEPT }))
          return { ok: true }
        case 'code':
          openPicker(CODE_EXTENSIONS.join(','), (file) => validateUploadFile(file, { extensions: CODE_EXTENSIONS }))
          return { ok: true }
        case 'camera':
          return openCamera()
        case 'other':
        default:
          return { ok: false, reason: 'No additional tools are registered yet.' }
      }
    },
    [navigate, openPicker, openCamera],
  )

  return (
    <>
      <SquareCodeCommandCenter
        quickAccess={quickAccess}
        divisions={divisions}
        onNavigate={onNavigate}
        onCommand={onCommand}
        onTool={onTool}
      />
      <input
        ref={fileInputRef}
        type="file"
        hidden
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) uploadFile(file)
        }}
      />
      {upload && (
        <div className="hub-upload-status" role="status" aria-live="polite">
          <div className="hub-upload-name">{upload.name}</div>
          <div className="hub-upload-bar">
            <span style={{ width: `${Math.round(upload.progress * 100)}%` }} data-status={upload.status} />
          </div>
          <div className="hub-upload-msg">
            {upload.status === 'uploading' && `Uploading ${Math.round(upload.progress * 100)}%`}
            {upload.status === 'done' && 'Uploaded'}
            {upload.status === 'error' && (upload.reason ?? 'Upload failed')}
          </div>
          <button
            type="button"
            className="hub-upload-close"
            onClick={() => setUpload(null)}
            aria-label="Dismiss upload status"
          >
            Close
          </button>
        </div>
      )}
      {camera.open && (
        <div className="hub-camera-overlay" role="dialog" aria-modal="true" aria-label="Camera capture">
          <div className="hub-camera-frame">
            {camera.error ? (
              <p className="hub-camera-error">{camera.error}</p>
            ) : (
              <video ref={videoRef} playsInline muted />
            )}
            <div className="hub-camera-actions">
              {!camera.error && (
                <button type="button" onClick={capturePhoto}>
                  Capture
                </button>
              )}
              <button type="button" onClick={closeCamera}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
